import { useEffect, useMemo, useState } from 'react';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CompanyCrmRoutingBindingDto } from '@/shared/api/companies';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { Notification } from '@/shared/ui';

import {
  applyPipelineToBinding,
  getPipelineStatuses,
  getRoutingChannelLabel,
  getRoutingChannelSupportId,
} from '../model/companyCrmRouting';
import {
  useCompanyCrmRouting,
  useUpdateCompanyCrmRouting,
} from '../model/useCompanyCrmRouting';

export const CompanyCrmRoutingSettings = ({
  companyId,
  embedded = false,
}: {
  companyId: string;
  embedded?: boolean;
}) => {
  const { t } = useTranslation();
  const { data, isPending, isError, error } = useCompanyCrmRouting(companyId);
  const updateMutation = useUpdateCompanyCrmRouting(companyId);
  const [bindings, setBindings] = useState<CompanyCrmRoutingBindingDto[]>([]);

  useEffect(() => {
    if (!data) return;
    setBindings(data.bindings.map((binding) => ({ ...binding })));
  }, [data]);

  const pipelineById = useMemo(
    () => new Map((data?.pipelines ?? []).map((pipeline) => [pipeline.id, pipeline])),
    [data?.pipelines]
  );

  const updateBinding = (
    channelId: string,
    updater: (binding: CompanyCrmRoutingBindingDto) => CompanyCrmRoutingBindingDto
  ) => {
    setBindings((current) =>
      current.map((binding) =>
        binding.channel_id === channelId ? updater(binding) : binding
      )
    );
  };

  const handleSave = async () => {
    if (!data) return;

    await updateMutation.mutateAsync({
      talks_priority: data.talks_priority,
      group_chats_enabled: data.group_chats_enabled,
      bindings: bindings.map((binding) => ({ ...binding })),
    });
  };

  if (isPending) return <Loader containerSize="medium" />;

  if (isError) {
    return (
      <>
        <NotFoundData title={t('company:crmRouting.loadError')} />
        <Notification open={isError} title={error?.message} />
      </>
    );
  }

  if (!data?.integration) {
    return <NotFoundData title={t('company:crmRouting.noIntegration')} />;
  }

  if (!bindings.length) {
    return <NotFoundData title={t('company:crmRouting.noChannels')} />;
  }

  return (
    <Box sx={{ mt: embedded ? 2 : 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={600}>
            {t('company:crmRouting.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.integration.crm_domain}
          </Typography>
        </Stack>
      </Stack>

      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflowX: 'auto',
        }}
      >
        <Table size="small" sx={{ minWidth: 1060 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 220 }}>
                {t('company:crmRouting.columns.channel')}
              </TableCell>
              <TableCell sx={{ width: 112 }}>
                {t('company:crmRouting.columns.enabled')}
              </TableCell>
              <TableCell sx={{ minWidth: 220 }}>
                {t('company:crmRouting.columns.pipeline')}
              </TableCell>
              <TableCell sx={{ minWidth: 220 }}>
                {t('company:crmRouting.columns.status')}
              </TableCell>
              <TableCell sx={{ minWidth: 220 }}>
                {t('company:crmRouting.columns.responsible')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bindings.map((binding) => {
              const statuses = getPipelineStatuses(
                data.pipelines,
                binding.pipeline_id
              );
              const channelSupportId = getRoutingChannelSupportId(binding);

              return (
                <TableRow key={binding.channel_id} hover>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography fontWeight={500}>
                        {getRoutingChannelLabel(binding)}
                      </Typography>
                      {channelSupportId ? (
                        <Typography variant="body2" color="text.secondary">
                          Support ID: {channelSupportId}
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={binding.binding_status}
                      onChange={(event) =>
                        updateBinding(binding.channel_id, (current) => ({
                          ...current,
                          binding_status: event.target.checked,
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={binding.pipeline_id ?? ''}
                        displayEmpty
                        onChange={(event) => {
                          const value = event.target.value;
                          const pipelineId = value === '' ? null : Number(value);
                          updateBinding(binding.channel_id, (current) =>
                            applyPipelineToBinding(
                              current,
                              pipelineId == null ? undefined : pipelineById.get(pipelineId)
                            )
                          );
                        }}
                      >
                        {data.pipelines.map((pipeline) => (
                          <MenuItem key={pipeline.id} value={pipeline.id}>
                            {pipeline.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={binding.pipeline_status || ''}
                        displayEmpty
                        onChange={(event) =>
                          updateBinding(binding.channel_id, (current) => ({
                            ...current,
                            pipeline_status: Number(event.target.value),
                          }))
                        }
                      >
                        {statuses.map((status) => (
                          <MenuItem key={status.id} value={status.id}>
                            {status.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={binding.responsible_user_id ?? ''}
                        displayEmpty
                        onChange={(event) => {
                          const value = event.target.value;
                          updateBinding(binding.channel_id, (current) => ({
                            ...current,
                            responsible_user_id: value === '' ? null : Number(value),
                          }));
                        }}
                      >
                        <MenuItem value="">
                          {t('company:crmRouting.defaultResponsible')}
                        </MenuItem>
                        {data.users.map((user) => (
                          <MenuItem key={user.id} value={Number(user.id)}>
                            {user.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2 }}>
        <Button
          variant="text"
          color="secondary"
          onClick={() => {
            setBindings(data.bindings.map((binding) => ({ ...binding })));
          }}
        >
          {t('company:crmRouting.cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          disabled={updateMutation.isPending}
          onClick={handleSave}
        >
          {t('company:crmRouting.apply')}
        </Button>
      </Stack>

      <Notification
        open={updateMutation.isError}
        title={updateMutation.error?.message}
      />
    </Box>
  );
};
